import {
  useAutosave,
  type AutosaveStatus,
  type UseAutosaveResult,
} from "@/hooks/use-autosave"
import type { AutosaveIdentity } from "@/lib/autosave"

export interface UseFormAutosaveOptions<T> {
  identity: AutosaveIdentity
  data: T
  isDirty: boolean
  onRestore: (draft: T) => void
  enabled?: boolean
  intervalMs?: number
  ttlMs?: number
  storage?: Storage
}

export interface UseFormAutosaveResult extends UseAutosaveResult {
  clearDraft: () => boolean
  saveIfDirty: () => boolean
  status: AutosaveStatus
}

export function useFormAutosave<T>({
  identity,
  data,
  isDirty,
  onRestore,
  ...options
}: UseFormAutosaveOptions<T>): UseFormAutosaveResult {
  const autosave = useAutosave({
    identity,
    state: data,
    dirty: isDirty,
    restore: onRestore,
    ...options,
  })

  // Sheets call this when they close.  Do not turn a clean close into a new
  // draft (which could otherwise resurrect already-persisted server values).
  return {
    ...autosave,
    clearDraft: autosave.clear,
    saveIfDirty: autosave.saveIfDirty,
  }
}